import React, { useEffect, useRef, useState } from "react";
import {
  Html5Qrcode,
  Html5QrcodeSupportedFormats,
  Html5QrcodeScannerState,
  Html5QrcodeScanType,
} from "html5-qrcode";

const extractLastSegment = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";

  try {
    const u = new URL(text);
    const segments = u.pathname.split("/").filter(Boolean);
    return segments.pop() || text;
  } catch {
    return text.replace(/\/+$/, "").split("/").pop();
  }
};

const defaultValidateCode = async () => true;

const QrScanner = ({
  onScanSuccess,
  onClose,
  isCodeInSystem,
  validateCode,
  closeOnSuccess = true,
  allowContinuousScan = false,
  title = "Scan code",
}) => {
  const [scans, setScans] = useState([]);
  const [toast, setToast] = useState("");
  const [isNarrow, setIsNarrow] = useState(window.innerWidth < 900);
  const [isLandscape, setIsLandscape] = useState(
    window.matchMedia &&
      window.matchMedia("(orientation: landscape)").matches
  );
  const [torchOn, setTorchOn] = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [selectedCamId, setSelectedCamId] = useState(null);
  const [starting, setStarting] = useState(false);

  const html5QrCodeRef = useRef(null);
  const fileInputRef = useRef(null);
  const videoTrackRef = useRef(null);
  const seenRawRef = useRef(new Set());
  const seenCodeRef = useRef(new Set());
  const lastTextRef = useRef({ t: "", at: 0 });
  const mountedRef = useRef(true);

  const effectiveValidate = validateCode || isCodeInSystem || defaultValidateCode;

  const showToast = (message, delay = 1500) => {
    setToast(message);
    if (delay > 0) {
      setTimeout(() => {
        if (mountedRef.current) setToast("");
      }, delay);
    }
  };

  const copyToClipboard = async (text, label = "📋 Copied") => {
    try {
      await navigator.clipboard.writeText(text || "");
      showToast(label, 1200);
    } catch {
      showToast("⚠️ Copy failed", 1200);
    }
  };

  const refreshTorchAvailability = () => {
    try {
      const video = document.querySelector("#qr-scanner-region video");
      const track = video?.srcObject?.getVideoTracks?.()[0] || null;
      videoTrackRef.current = track;

      const caps = track?.getCapabilities?.();
      setTorchAvailable(!!(caps && "torch" in caps));
    } catch {
      videoTrackRef.current = null;
      setTorchAvailable(false);
    }
  };

  const stopScanner = async () => {
    const inst = html5QrCodeRef.current;
    if (!inst) return;

    try {
      const state = inst.getState?.();
      if (
        state === Html5QrcodeScannerState.SCANNING ||
        state === Html5QrcodeScannerState.PAUSED
      ) {
        await inst.stop();
      }
    } catch (err) {
      console.warn("stopScanner error:", err);
    }
  };

  const clearScanner = async () => {
    const inst = html5QrCodeRef.current;
    if (!inst) return;

    try {
      await inst.clear();
    } catch (err) {
      console.warn("clearScanner error:", err);
    }
  };

  const listCameras = async () => {
    try {
      const cams = await Html5Qrcode.getCameras();
      if (!mountedRef.current) return;

      setCameras(cams || []);

      if (!selectedCamId && cams?.length) {
        const back = cams.find((c) => /back|rear|trase|задн|environment/i.test(c.label || ""));
        setSelectedCamId((back || cams[0]).id);
      }
    } catch (err) {
      console.error("getCameras failed:", err);
      showToast("Greška pri pristupu kamerama", 2000);
    }
  };

  const handleDecoded = async (decodedText, decodedResult) => {
    const now = Date.now();

    if (
      decodedText === lastTextRef.current.t &&
      now - lastTextRef.current.at < 1200
    ) {
      return;
    }

    lastTextRef.current = { t: decodedText, at: now };

    const formatName =
      decodedResult?.result?.format?.formatName ||
      decodedResult?.result?.format ||
      decodedResult?.decodedResult?.result?.format?.formatName ||
      "UNKNOWN";

    const code = extractLastSegment(decodedText);

    if (!code) return;
    if (seenRawRef.current.has(decodedText) || seenCodeRef.current.has(code)) {
      return;
    }

    let known = true;
    try {
      known = await effectiveValidate(code);
    } catch (err) {
      console.warn("validate code error:", err);
      known = false;
    }

    seenRawRef.current.add(decodedText);
    seenCodeRef.current.add(code);

    const scanEntry = {
      format: formatName,
      rawText: decodedText,
      code,
      known,
      time: new Date(),
    };

    setScans((prev) => [scanEntry, ...prev.slice(0, 19)]);

    if (typeof onScanSuccess === "function") {
      await onScanSuccess(code, scanEntry);
    }

    if (known) {
      showToast("✅ Kod prepoznat", 1000);
    } else {
      showToast("⚠️ Kod nije prepoznat", 1200);
    }

    if (closeOnSuccess && known && !allowContinuousScan) {
      await stopScanner();
      onClose?.();
    }
  };

  const startScanner = async (cameraId) => {
    const inst = html5QrCodeRef.current;
    if (!inst || !cameraId || starting) return;

    try {
      setStarting(true);
      await stopScanner();
      await clearScanner();

      const config = {
        fps: 10,
        qrbox: (vp) => {
          const side = Math.min(vp.width, vp.height);
          const size = Math.max(220, Math.floor(side - 20));
          return { width: size, height: size };
        },
        formatsToSupport: Object.values(Html5QrcodeSupportedFormats),
        experimentalFeatures: { useBarCodeDetectorIfSupported: true },
        rememberLastUsedCamera: false,
      };

      await inst.start(
        cameraId,
        config,
        async (decodedText, decodedResult) => {
          await handleDecoded(decodedText, decodedResult);
        },
        (errMsg) => {
          if (String(errMsg || "").includes("NotFoundException")) return;
          console.warn("scan error:", errMsg);
        }
      );

      setTimeout(() => {
        if (!mountedRef.current) return;
        refreshTorchAvailability();
      }, 300);
    } catch (err) {
      console.error("startScanner failed:", err);
      showToast("Ne mogu pristupiti kameri", 2000);
      setTorchAvailable(false);
    } finally {
      if (mountedRef.current) setStarting(false);
    }
  };

  const toggleTorch = async () => {
    const track = videoTrackRef.current;
    if (!track) {
      showToast("🔦 Nije pronađen video stream", 1200);
      return;
    }

    try {
      const capabilities = track.getCapabilities?.();
      if (!capabilities?.torch) {
        showToast("🔦 Lampica nije podržana", 1200);
        return;
      }

      await track.applyConstraints({
        advanced: [{ torch: !torchOn }],
      });

      setTorchOn((prev) => !prev);
      showToast(!torchOn ? "🔦 Lampica uključena" : "🔦 Lampica isključena", 1200);
    } catch (err) {
      console.error("toggleTorch error:", err);
      showToast("⚠️ Ne mogu kontrolisati lampicu", 1500);
    }
  };

  const onPickImage = () => fileInputRef.current?.click();

  const onImageSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    e.target.value = "";
    showToast("🔄 Obrada slike...", 0);

    try {
      if (!file.type.startsWith("image/")) {
        throw new Error("Izabrani fajl nije slika");
      }

      const scannerRegion = document.getElementById("qr-scanner-region");
      if (scannerRegion) {
        scannerRegion.innerHTML = "";
        const imgElement = document.createElement("img");
        imgElement.src = URL.createObjectURL(file);
        imgElement.style.maxWidth = "100%";
        imgElement.style.maxHeight = "100%";
        imgElement.style.objectFit = "contain";
        scannerRegion.appendChild(imgElement);
      }

      const result = await html5QrCodeRef.current.scanFileV2(file, true);

      if (!result?.decodedText) {
        throw new Error("Nije pronađen QR ili barkod u slici");
      }

      await handleDecoded(result.decodedText, {
        result: {
          format: result.result?.format?.formatName || "IMAGE",
        },
      });

      showToast("✅ Kod pronađen!", 1500);
    } catch (err) {
      console.error("scan file error:", err);
      showToast(`⚠️ ${err.message || "Greška pri obradi slike"}`, 2500);

      const scannerRegion = document.getElementById("qr-scanner-region");
      if (scannerRegion) scannerRegion.innerHTML = "";
      if (selectedCamId) {
        await startScanner(selectedCamId);
      }
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    html5QrCodeRef.current = new Html5Qrcode("qr-scanner-region");
    listCameras();

    return () => {
      mountedRef.current = false;
      stopScanner();
    };
  }, []);

  useEffect(() => {
    const onResize = () => {
      setIsNarrow(window.innerWidth < 900);
      setIsLandscape(
        window.matchMedia &&
          window.matchMedia("(orientation: landscape)").matches
      );
    };

    window.addEventListener("resize", onResize);
    const mq = window.matchMedia("(orientation: landscape)");
    const onOrient = (e) => setIsLandscape(e.matches);
    mq.addEventListener("change", onOrient);

    return () => {
      window.removeEventListener("resize", onResize);
      mq.removeEventListener("change", onOrient);
    };
  }, []);

  useEffect(() => {
    if (!selectedCamId || !html5QrCodeRef.current) return;
    startScanner(selectedCamId);
  }, [selectedCamId]);

  const last = scans[0];
  const showSidePanel = !isNarrow || isLandscape;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.85)",
        zIndex: 9999,
        display: "grid",
        gridTemplateColumns: showSidePanel ? "1fr 360px" : "1fr",
        gap: showSidePanel ? 16 : 0,
        alignItems: "center",
        justifyItems: "center",
        padding: showSidePanel ? "24px" : "16px",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 600,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            color: "#fff",
            fontSize: 20,
            fontWeight: 700,
            marginBottom: 12,
            textAlign: "center",
          }}
        >
          {title}
        </div>

        <div
          style={{
            position: "relative",
            width: "100%",
            aspectRatio: "1 / 1",
            maxWidth: 500,
            background: "#000",
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 12,
              left: 12,
              right: 12,
              display: "flex",
              justifyContent: "space-between",
              zIndex: 10,
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={onPickImage}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                background: "rgba(0,0,0,0.7)",
                color: "#fff",
                border: "1px solid #555",
                cursor: "pointer",
              }}
            >
              🖼️ Image
            </button>

            {cameras.length > 1 && (
              <select
                value={selectedCamId || ""}
                onChange={(e) => setSelectedCamId(e.target.value)}
                style={{
                  padding: "8px 12px",
                  borderRadius: 8,
                  background: "rgba(0,0,0,0.7)",
                  color: "#fff",
                  border: "1px solid #555",
                  cursor: "pointer",
                  maxWidth: 220,
                }}
              >
                {cameras.map((camera) => (
                  <option key={camera.id} value={camera.id}>
                    {camera.label || "Camera"}
                  </option>
                ))}
              </select>
            )}

            {torchAvailable && (
              <button
                onClick={toggleTorch}
                style={{
                  padding: "8px 12px",
                  borderRadius: 8,
                  background: "rgba(0,0,0,0.7)",
                  color: "#fff",
                  border: "1px solid #555",
                  cursor: "pointer",
                }}
              >
                {torchOn ? "💡 ON" : "💡 OFF"}
              </button>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={onImageSelected}
          />

          <div
            id="qr-scanner-region"
            style={{
              width: "100%",
              height: "100%",
            }}
          />

          <div
            style={{
              position: "absolute",
              bottom: 12,
              left: 12,
              right: 12,
              display: "flex",
              gap: 8,
              justifyContent: "center",
              zIndex: 10,
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={onClose}
              style={{
                padding: "8px 16px",
                backgroundColor: "#f44336",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
              }}
            >
              Close
            </button>

            {last && (
              <>
                <button
                  onClick={() => copyToClipboard(last.rawText, "📋 Raw copied")}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: "#333",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    cursor: "pointer",
                  }}
                >
                  Copy Raw
                </button>
                <button
                  onClick={() => copyToClipboard(last.code, "📋 Code copied")}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: "#333",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    cursor: "pointer",
                  }}
                >
                  Copy Code
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {showSidePanel ? (
        <div
          style={{
            width: 360,
            height: "80vh",
            overflowY: "auto",
            background: "#111",
            color: "#eee",
            borderRadius: 10,
            border: "1px solid #333",
            padding: 12,
          }}
        >
          <h3 style={{ marginTop: 0 }}>📋 Scan History</h3>

          {scans.length === 0 && (
            <div style={{ color: "#999", fontSize: 14 }}>
              Još nema skenova.
            </div>
          )}

          {scans.map((s, i) => (
            <div
              key={i}
              style={{
                marginTop: 8,
                padding: 12,
                background: "#1a1a1a",
                borderRadius: 8,
                borderLeft: `4px solid ${s.known ? "#4CAF50" : "#FF9800"}`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 6,
                }}
              >
                <span><b>{s.format}</b></span>
                <span
                  style={{
                    color: s.known ? "#4CAF50" : "#FF9800",
                    fontWeight: "bold",
                  }}
                >
                  {s.known ? "KNOWN" : "UNKNOWN"}
                </span>
              </div>

              <div
                style={{
                  fontSize: 12,
                  color: "#aaa",
                  marginBottom: 8,
                }}
              >
                {s.time.toLocaleTimeString()}
              </div>

              <div style={{ wordBreak: "break-word", marginBottom: 8 }}>
                <div style={{ marginBottom: 4 }}><b>Raw:</b></div>
                <div
                  style={{
                    background: "#222",
                    padding: 6,
                    borderRadius: 4,
                    fontSize: 12,
                  }}
                >
                  {s.rawText}
                </div>
              </div>

              <div style={{ wordBreak: "break-word" }}>
                <div style={{ marginBottom: 4 }}><b>Code:</b></div>
                <div
                  style={{
                    background: "#222",
                    padding: 6,
                    borderRadius: 4,
                    fontSize: 12,
                  }}
                >
                  {s.code}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  marginTop: 12,
                }}
              >
                <button
                  onClick={() => copyToClipboard(s.rawText, "📋 Raw copied")}
                  style={{
                    flex: 1,
                    background: "#333",
                    color: "#fff",
                    border: "none",
                    borderRadius: 4,
                    padding: "6px 0",
                    cursor: "pointer",
                    fontSize: 12,
                  }}
                >
                  Copy Raw
                </button>
                <button
                  onClick={() => copyToClipboard(s.code, "📋 Code copied")}
                  style={{
                    flex: 1,
                    background: "#333",
                    color: "#fff",
                    border: "none",
                    borderRadius: 4,
                    padding: "6px 0",
                    cursor: "pointer",
                    fontSize: 12,
                  }}
                >
                  Copy Code
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div
          style={{
            width: "100%",
            height: "30vh",
            overflowY: "auto",
            background: "#111",
            color: "#eee",
            borderRadius: 10,
            border: "1px solid #333",
            padding: 12,
            marginTop: 12,
          }}
        >
          <h3 style={{ marginTop: 0, fontSize: 16 }}>📋 Recent Scans</h3>

          {scans.length === 0 && (
            <div style={{ color: "#999", fontSize: 13 }}>
              Još nema skenova.
            </div>
          )}

          {scans.slice(0, 3).map((s, i) => (
            <div
              key={i}
              style={{
                marginTop: 8,
                padding: 8,
                background: "#1a1a1a",
                borderRadius: 6,
                borderLeft: `3px solid ${s.known ? "#4CAF50" : "#FF9800"}`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 12,
                }}
              >
                <span>{s.format}</span>
                <span
                  style={{
                    color: s.known ? "#4CAF50" : "#FF9800",
                    fontWeight: "bold",
                  }}
                >
                  {s.known ? "KNOWN" : "UNKNOWN"}
                </span>
              </div>

              <div
                style={{
                  fontSize: 10,
                  color: "#aaa",
                  margin: "4px 0",
                }}
              >
                {s.time.toLocaleTimeString()}
              </div>

              <div
                style={{
                  wordBreak: "break-word",
                  fontSize: 11,
                  marginBottom: 4,
                }}
              >
                <b>Code:</b> {s.code}
              </div>
            </div>
          ))}
        </div>
      )}

      {toast && (
        <div
          style={{
            position: "fixed",
            right: 16,
            bottom: 16,
            background: "#333",
            color: "#fff",
            padding: "10px 16px",
            borderRadius: 8,
            boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
            zIndex: 10000,
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
};

export default QrScanner;